import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import Modal from "../modal/Modal";
import ApprovalControl from "../approval-control/ApprovalControl";
import OfferSummaryCard, {
  type OfferSummaryCardData,
} from "./OfferSummaryCard";
import SubcontractContractPdfPreview, {
  type SubcontractPdfPreviewState,
} from "./SubcontractContractPdfPreview";
import {
  isSubcontractOfferForm,
  selectLatestContractFile,
  useGetDmThreadDetailQuery,
  type DmThread,
} from "../../app/services/dmThreads";
import {
  formatPdfExportError,
  requestOfferSubcontractPdf,
} from "../../app/services/pdfExports";

interface OfferReceiveModalProps {
  thread: DmThread;
  /** 詳細 API を引くためのプロジェクト ID（仕様書 §2 Path Parameter） */
  projectId: string;
  onClose: () => void;
  onAccept: () => void;
  isAccepting?: boolean;
  errorMessage?: string | null;
  /**
   * 読み取り専用モード。`true` の場合は引受 UI（`ApprovalControl` と
   * 「引き受ける」プライマリアクション）を非表示にし、PDF / サマリの
   * 確認だけを行うモーダルとして開く。
   *
   * タイムライン内の契約書アイテムから「内容を確認」で開くときに使う
   * （contractor_pending 以外のフェーズや送信者からも開けるようにするため）。
   */
  readOnly?: boolean;
}

// 承認 UI はスクロール領域の下端に sticky で固定し、フッターの真上に
// 常に表示する。Body の左右 padding を負 margin で打ち消し、上下 padding は
// 持たない（Body の padding-bottom 12px とフッターの border でフレーミング）。
const ApprovalRow = styled.div`
  position: sticky;
  bottom: 0;
  margin: 0 -12px;
  padding: 0 12px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  z-index: 1;
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
`;

const SUBCONTRACT_FORBIDDEN_MESSAGE =
  "契約書 PDF を出力する権限がありません (403)。送信者・受託者本人またはプロジェクトの MASTERADMIN/OWNER/PRODUCER 権限が必要です。";

/**
 * オファー受領モーダル。
 * 仕様書 dm-threads.md §5「contractor_pending → in_progress」の引受フローに対応する。
 *
 * 表示条件は呼び出し元で判定する想定:
 *  - `thread.thread_type === "subcontract"`
 *  - `thread.status === "contractor_pending"`
 *  - `thread.contractor.id === currentUserId`
 *
 * モーダル内では契約書 PDF をインラインプレビューする（ダウンロードリンクは
 * 受領モーダル外＝メッセンジャーのオファー詳細行に分離する設計）。
 */
const OfferReceiveModal: React.FC<OfferReceiveModalProps> = ({
  thread,
  projectId,
  onClose,
  onAccept,
  isAccepting = false,
  errorMessage,
  readOnly = false,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Detail API で `offer_form` などの拡張ブロックを取得し、サマリ表示を充実させる。
  // 失敗時は thread 直下のフィールドのみで描画するフォールバックに任せる。
  const { data: detailData, isLoading: isDetailLoading } =
    useGetDmThreadDetailQuery({
      project_id: projectId,
      dm_id: thread.id,
    });
  const offerForm = detailData?.data.offer_form ?? null;
  // 送信時に BE が保存した契約書ドラフト（送信者サインのみ）の URL + ファイル名。
  const savedContractFile = useMemo(
    () => selectLatestContractFile(detailData?.data?.contract_files),
    [detailData],
  );
  const subcontractOfferForm = isSubcontractOfferForm(offerForm, thread.thread_type)
    ? offerForm
    : null;

  // ─── 契約書PDF プレビュー ────────────────────────────
  const showPdfPreview = thread.thread_type === "subcontract";
  const [pdfPreview, setPdfPreview] = useState<SubcontractPdfPreviewState>({
    status: "generating",
  });

  const fetchPdf = useCallback(
    async (signal?: { cancelled: boolean }) => {
      setPdfPreview({ status: "generating" });
      try {
        const { blob, filename } = await requestOfferSubcontractPdf({
          dmId: thread.id,
        });
        if (signal?.cancelled) return;
        setPdfPreview({ status: "ready", blob, filename });
      } catch (e) {
        if (signal?.cancelled) return;
        console.error("[OfferReceiveModal] subcontract PDF preview failed", e);
        setPdfPreview({
          status: "error",
          errorMessage: formatPdfExportError(e, {
            forbiddenMessage: SUBCONTRACT_FORBIDDEN_MESSAGE,
          }),
        });
      }
    },
    [thread.id],
  );

  // モーダルマウント時 + thread.id 切替時に PDF を取得（再オープン時にも再フェッチ）。
  useEffect(() => {
    if (!showPdfPreview) return;
    // Detail 取得中は「生成中」表示で待ち、無駄な動的生成を避ける。
    if (isDetailLoading) {
      setPdfPreview({ status: "generating" });
      return;
    }
    // 保存済み PDF があればそれを表示。無ければ動的生成にフォールバック。
    if (savedContractFile) {
      setPdfPreview({
        status: "ready",
        url: savedContractFile.url,
        filename: savedContractFile.filename,
      });
      return;
    }
    const signal = { cancelled: false };
    void fetchPdf(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [showPdfPreview, isDetailLoading, savedContractFile, fetchPdf]);

  const handleRetryPdf = useCallback(() => {
    void fetchPdf();
  }, [fetchPdf]);

  const summaryData: OfferSummaryCardData = {
    recipientLabel: thread.requester?.display_name ?? null,
    recipientAvatarUrl: thread.requester?.avatar_url ?? null,
    assignedRoles: thread.assigned_roles,
    scope: thread.scope,
    deadline: thread.deadline,
    contractAmount: thread.contract_amount,
    advancePaymentRate: thread.advance_payment_rate,
    acceptPosition: thread.accept_position,
    requiresDeliverable: thread.requires_deliverable,
    allowSubcontracting: thread.allow_subcontracting,
    // initial_message は Detail API の `offer_form` を優先（より新しい値）。
    initialMessage:
      subcontractOfferForm?.initial_message ?? thread.initial_message,
  };

  // PDF が「未指定／エラー」のときはサマリカードへフォールバックして
  // 何を引き受けるのかが必ず確認できる状態を保つ。
  const showSummaryCard = !showPdfPreview || pdfPreview.status === "error";

  return (
    <Modal
      onClose={isAccepting ? undefined : onClose}
      title={readOnly ? "契約書の内容を確認" : "オファーを受け取りました"}
      description={
        readOnly
          ? "送信された契約書の内容を確認できます。"
          : "内容を確認して承認すると、次へ進めます。"
      }
      size="default"
      primaryAction={
        readOnly
          ? {
              label: "閉じる",
              onClick: onClose,
            }
          : {
              label: isAccepting ? "処理中..." : "引き受ける",
              onClick: onAccept,
              disabled: !isConfirmed || isAccepting,
              rightIcon: "send",
            }
      }
      secondaryAction={
        readOnly
          ? undefined
          : {
              label: "戻る",
              onClick: onClose,
              disabled: isAccepting,
            }
      }
    >
      {showPdfPreview && (
        <SubcontractContractPdfPreview
          preview={pdfPreview}
          onRetry={handleRetryPdf}
        />
      )}

      {showSummaryCard && (
        <OfferSummaryCard data={summaryData} showRecipient />
      )}

      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

      {!readOnly && (
        <ApprovalRow>
          <ApprovalControl
            onConfirm={() => setIsConfirmed(true)}
            onConfirmedChange={setIsConfirmed}
            disabled={isAccepting}
          />
        </ApprovalRow>
      )}
    </Modal>
  );
};

export default OfferReceiveModal;
